import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import type { Listing } from "@/types";

interface Meta { product_types: Record<string, string>; collections: Record<string, string>; posts: { slug: string; title: string; printable_listing?: string | null }[]; listings: { id: string; name: string }[] }

function Picker({ label, options, value, onChange, testId }: { label: string; options: { id: string; label: string; note?: string }[]; value: string[]; onChange: (v: string[]) => void; testId: string }) {
  const [q, setQ] = useState("");
  const [kept] = useState(() => new Set(value));
  const shown = options.filter((o) => value.includes(o.id) || kept.has(o.id) || (q && o.label.toLowerCase().includes(q.toLowerCase()))).slice(0, 30);
  const flip = (id: string) => { kept.add(id); onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]); };
  return (
    <div data-testid={testId}>
      <p className="mb-1 text-sm font-bold text-[#23324A]">{label}</p>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" className="mb-2 w-full rounded-xl border-2 border-[#23324A]/20 px-3 py-1.5 text-sm" data-testid={`${testId}-search`} />
      <div className="max-h-44 space-y-1 overflow-y-auto">
        {shown.map((o) => (
          <label key={o.id} className="flex items-start gap-2 text-sm text-[#23324A]">
            <input type="checkbox" checked={value.includes(o.id)} onChange={() => flip(o.id)} className="mt-1" data-testid={`${testId}-opt-${o.id}`} />
            <span>{o.label}{o.note && <span className="text-xs text-[#C2412D]"> · {o.note}</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function CatalogPanel({ listing, onSaved }: { listing: Listing; onSaved: (l: Listing) => void }) {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [f, setF] = useState({ product_type: listing.product_type || "", collection: listing.collection || "", related_ids: listing.related_ids || [], bundle_ids: listing.bundle_ids || [], tutorial_slugs: [] as string[] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/catalog/meta").then((r) => { setMeta(r.data); setF((x) => ({ ...x, tutorial_slugs: r.data.posts.filter((p: Meta["posts"][0]) => p.printable_listing === listing.id).map((p: Meta["posts"][0]) => p.slug) })); }).catch(() => {});
  }, [listing.id]);
  if (!meta) return null;
  const others = meta.listings.filter((l) => l.id !== listing.id).map((l) => ({ id: l.id, label: l.name }));
  const posts = meta.posts.map((p) => ({ id: p.slug, label: p.title, note: p.printable_listing && p.printable_listing !== listing.id ? "linked to another printable" : undefined }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/listings/${listing.id}/catalog`, f); onSaved(data); toast.success("Catalog details saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const sel = "w-full rounded-xl border-2 border-[#23324A]/20 bg-white px-3 py-2 text-sm";
  return (
    <div className="paper-card space-y-4 p-5" data-testid="catalog-panel">
      <h2 className="font-display text-lg font-extrabold text-[#23324A]">Shop catalog</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm font-bold text-[#23324A]">Product type
          <select className={sel} value={f.product_type} onChange={(e) => setF({ ...f, product_type: e.target.value })} data-testid="catalog-type">
            <option value="">Choose…</option>{Object.entries(meta.product_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="text-sm font-bold text-[#23324A]">Design collection
          <select className={sel} value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} data-testid="catalog-collection">
            <option value="">None</option>{Object.entries(meta.collections).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>
      <Picker label="Tutorials that use this printable" options={posts} value={f.tutorial_slugs} onChange={(v) => setF({ ...f, tutorial_slugs: v })} testId="catalog-tutorials" />
      <Picker label="Related products (Complete the set)" options={others} value={f.related_ids} onChange={(v) => setF({ ...f, related_ids: v })} testId="catalog-related" />
      {f.product_type === "bundle" && <Picker label="This bundle includes" options={others} value={f.bundle_ids} onChange={(v) => setF({ ...f, bundle_ids: v })} testId="catalog-bundle" />}
      <button className="btn-coral" disabled={busy} onClick={save} data-testid="catalog-save">{busy ? "Saving…" : "Save catalog details"}</button>
    </div>
  );
}
